import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { imgSize200 } from 'constants/image';
import useMedia from 'hooks/useMedia';
import { formatImagelink } from 'utils';
import { DateUtils } from 'utils/DateUtils';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { REVIEWS_BLOCK_ID } from '@Modules/publicSellerOffers/constants';
import type { SellerType } from '@Modules/offers/types';

import { Avatar } from '@Atomic/atoms/Avatar';
import { ConditionalWrap } from '@Atomic/atoms/ConditionalWrap';
import { Rate } from '@Atomic/atoms/Rate';
import { Text } from '@Atomic/atoms/Text';

import { ProfileLinkWrapper } from '../ProfileLinkWrapper';

import styles from './offerOwner.module.scss';

type OfferOwnerProps = WithTestId<{
  seller: SellerType;
  variant?: 'default' | 'short';
  withLinks?: boolean;
  className?: string;
}>;

export const OfferOwner: FC<OfferOwnerProps> = ({
  className,
  seller,
  variant = 'default',
  withLinks = false,
  testId = 'offer-owner',
}) => {
  const { t } = useTranslation();
  const { xs } = useMedia();

  if (!seller) {
    return null;
  }

  const { avatar, name, registeredDate, review, activity } = seller;

  const img = avatar ? formatImagelink(avatar.cropUrlTemplate, imgSize200) : '';

  const isOnline = !!activity?.online;
  const responseTime = activity?.responseTimeTitle;
  const hasReviews = (review?.totalCount ?? 0) > 0;

  return (
    <div
      className={cn(
        styles.offerOwner,
        styles[`variant-${variant}`],
        {
          [styles.withLinks]: withLinks,
        },
        className
      )}
      data-testid={testId}
    >
      <div className={styles.info}>
        <ConditionalWrap
          condition={withLinks}
          wrapper={<ProfileLinkWrapper uuid={seller.uuid} withIcon className={styles.link} />}
        >
          <span className={styles.nameRow}>
            <Text className={styles.name} testId={getTestIdByParts(testId, 'name')}>
              {name || t('offer.guest')}
            </Text>
            {isOnline && (
              <Text className={styles.online} testId={getTestIdByParts(testId, 'online')}>
                {t('offer.online')}
              </Text>
            )}
          </span>
        </ConditionalWrap>

        <div className={styles.meta}>
          {/* The mobile `default` layout (SellerDetails) omits response time
              entirely — gate in JSX so the node isn't rendered then hidden. */}
          {variant !== 'default' && responseTime && (
            <Text
              className={styles.responseTime}
              testId={getTestIdByParts(testId, 'response-time')}
            >
              {responseTime}
            </Text>
          )}

          {hasReviews && (
            <ProfileLinkWrapper
              uuid={seller.uuid}
              goto={REVIEWS_BLOCK_ID}
              className={styles.rating}
              testId={getTestIdByParts(testId, 'rating')}
            >
              <Rate
                variant="value-reverse"
                size="medium"
                value={review.score}
                className={styles.rate}
              />
              <Text variant="large" className={styles.reviewsCount}>
                {t('publicSeller.reviewsCount', { count: review?.totalCount || 0 })}
              </Text>
            </ProfileLinkWrapper>
          )}

          {variant === 'short' && (
            <Text className={styles.offersCount} testId={getTestIdByParts(testId, 'offers-count')}>
              {t('offer.activeOffersCount', { count: seller.offerActiveCount })}
            </Text>
          )}

          {variant === 'default' && (
            <>
              <Text
                className={styles.offersCount}
                testId={getTestIdByParts(testId, 'offers-count')}
                variant={xs ? 'small' : 'default'}
              >
                {t('favoriteSeller.offersCount', { count: seller.offerActiveCount })}
              </Text>

              {registeredDate && (
                <Text
                  className={styles.registeredDate}
                  testId={getTestIdByParts(testId, 'date')}
                  variant={xs ? 'small' : 'default'}
                >
                  {t('offer.onPlatform')} {DateUtils.format(registeredDate, 'DD.MM.YYYY')}
                </Text>
              )}
            </>
          )}
        </div>
      </div>

      <div className={cn(styles.avatar, { [styles.avatarOnline]: isOnline })}>
        <ConditionalWrap
          condition={withLinks}
          wrapper={<ProfileLinkWrapper uuid={seller.uuid} className={styles.link} />}
        >
          <Avatar
            src={img}
            alt={name || 'seller'}
            className={styles.image}
            data-testid={getTestIdByParts(testId, 'avatar')}
          />
        </ConditionalWrap>
      </div>
    </div>
  );
};
